import dayjs from "dayjs";

export const PAGE_SIZE = 50;

export const pick = (row, ...keys) => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
  }
  return undefined;
};

export const toNumber = (value, fallback = null) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

export const toBoolean = (value) =>
  value === true || value === 1 || value === "1" || String(value).toLowerCase() === "true";

export const getLawFlag = (law, index) => {
  if (!law || law.length <= index) return 1;
  return toNumber(law[index], 1);
};

export const normalizeOption = (row, idKeys, labelKeys) => ({
  value: toNumber(pick(row, ...idKeys)),
  label: String(pick(row, ...labelKeys) ?? "")
});

export const normalizeLookups = (data) => {
  const source = data || {};
  const map = (keys, idKeys, labelKeys) => {
    const rows = pick(source, ...keys);
    return (Array.isArray(rows) ? rows : [])
      .map((row) => normalizeOption(row, idKeys, labelKeys))
      .filter((item) => item.value != null);
  };
  return {
    warehouses: map(
      ["warehouses", "Warehouses", "sklads", "Sklads"],
      ["idSkl", "IdSkl", "id", "Id"],
      ["warehouseName", "WarehouseName", "name", "Name"]
    ),
    documentTypes: map(
      ["documentTypes", "DocumentTypes", "vids", "Vids"],
      ["vid", "Vid", "id", "Id"],
      ["name", "Name", "label", "Label"]
    )
  };
};

export const normalizePage = (data, normalizer) => {
  const rawItems = Array.isArray(data) ? data : pick(data, "items", "Items");
  const items = (Array.isArray(rawItems) ? rawItems : []).map(normalizer);
  return {
    items,
    hasMore: Array.isArray(data)
      ? items.length >= PAGE_SIZE
      : toBoolean(pick(data, "hasMore", "HasMore"))
  };
};

export const normalizeDocument = (row = {}) => {
  const rawLines = pick(row, "lines", "Lines", "items", "Items");
  return {
    ...row,
    idDoc: toNumber(pick(row, "idDoc", "IdDoc")),
    docdate: pick(row, "docdate", "Docdate") ?? null,
    vid: toNumber(pick(row, "vid", "Vid")),
    idFrom: toNumber(pick(row, "idFrom", "IdFrom")),
    fromName: pick(row, "fromName", "FromName") ?? "",
    idDest: toNumber(pick(row, "idDest", "IdDest")),
    destName: pick(row, "destName", "DestName") ?? "",
    isAct: toBoolean(pick(row, "isAct", "IsAct")),
    komment: pick(row, "komment", "Komment") ?? "",
    syma: toNumber(pick(row, "syma", "Syma"), 0),
    sfDocnum: pick(row, "sfDocnum", "SfDocnum", "SFDocnum", "SfDocNum") ?? "",
    sfDocdate: pick(row, "sfDocdate", "SfDocdate", "SFDocdate", "SfDocDate") ?? null,
    lines: (Array.isArray(rawLines) ? rawLines : []).map(normalizeLine)
  };
};

export const normalizeLine = (row = {}, index = 0) => ({
  ...row,
  key: String(pick(row, "idStr", "IdStr") ?? `new-${index}`),
  idStr: toNumber(pick(row, "idStr", "IdStr")),
  idTov: toNumber(pick(row, "idTov", "IdTov")),
  name: pick(row, "name", "Name") ?? "",
  funit: pick(row, "funit", "Funit") ?? "",
  kol: toNumber(pick(row, "kol", "Kol"), 0),
  kolFact: toNumber(pick(row, "kolFact", "KolFact"), 0),
  zena: toNumber(pick(row, "zena", "Zena"), 0),
  koeff: toNumber(pick(row, "koeff", "Koeff"), 1),
  syma: toNumber(pick(row, "syma", "Syma"), 0),
  kolDiff: toNumber(pick(row, "kolDiff", "KolDiff"), 0)
});

export const normalizeRegisterRow = (row = {}, index = 0) => ({
  ...row,
  key: String(pick(row, "idTov", "IdTov") ?? index),
  idTov: toNumber(pick(row, "idTov", "IdTov")),
  tovName: pick(row, "tovName", "TovName") ?? "",
  funit: pick(row, "funit", "Funit") ?? "",
  kolBeg: toNumber(pick(row, "kolBeg", "KolBeg"), 0),
  kolPut: toNumber(pick(row, "kolPut", "KolPut"), 0),
  kolExp: toNumber(pick(row, "kolExp", "KolExp"), 0),
  kolEnd: toNumber(pick(row, "kolEnd", "KolEnd"), 0)
});

const MOVE_VID_LABELS = {
  1: "Приход",
  "-1": "Расход",
  3: "Приход (перемещение)",
  "-3": "Расход (перемещение)",
  4: "Оприходование недостачи",
  "-4": "Списание излишков"
};

export const formatMoveVid = (vid) => MOVE_VID_LABELS[String(vid)] ?? String(vid ?? "");

export const normalizeMovementRow = (row = {}, index = 0) => ({
  ...row,
  key: String(pick(row, "idReg", "IdReg") ?? `move-${index}`),
  idReg: toNumber(pick(row, "idReg", "IdReg")),
  idSkl: toNumber(pick(row, "idSkl", "IdSkl")),
  sklName: pick(row, "sklName", "SklName") ?? "",
  fdate: pick(row, "fdate", "Fdate") ?? null,
  vid: toNumber(pick(row, "vid", "Vid"), 0),
  vidOsn: toNumber(pick(row, "vidOsn", "VidOsn"), 0),
  idOsn: toNumber(pick(row, "idOsn", "IdOsn")),
  idOsnStr: toNumber(pick(row, "idOsnStr", "IdOsnStr")),
  kol: toNumber(pick(row, "kol", "Kol"), 0),
  idTov: toNumber(pick(row, "idTov", "IdTov")),
  tovName: pick(row, "tovName", "TovName") ?? "",
  funit: pick(row, "funit", "Funit") ?? "",
  signedKol:
    toNumber(pick(row, "vid", "Vid"), 0) < 0
      ? -Math.abs(toNumber(pick(row, "kol", "Kol"), 0))
      : Math.abs(toNumber(pick(row, "kol", "Kol"), 0)),
  direction: toNumber(pick(row, "vid", "Vid"), 0) < 0 ? "Расход" : "Приход",
  vidLabel: formatMoveVid(toNumber(pick(row, "vid", "Vid"), 0)),
  sourceLabel:
    toNumber(pick(row, "vidOsn", "VidOsn"), 0) === 1
      ? `Накладная #${pick(row, "idOsn", "IdOsn") ?? ""}`
      : `Источник ${pick(row, "vidOsn", "VidOsn") ?? ""} #${pick(row, "idOsn", "IdOsn") ?? ""}`
});

export const formatDate = (value) => {
  const date = dayjs(value);
  return value && date.isValid() ? date.format("DD.MM.YYYY") : "";
};

export const formatNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number)
    ? number.toLocaleString("ru-RU", { maximumFractionDigits: 3 })
    : "";
};

export const normalizeRestCheck = (data = {}) => ({
  mode: toNumber(pick(data, "mode", "Mode"), 0),
  items: (pick(data, "items", "Items") || []).map((row) => ({
    idTov: toNumber(pick(row, "idTov", "IdTov")),
    name: pick(row, "name", "Name") ?? "",
    funit: pick(row, "funit", "Funit") ?? "",
    kol: toNumber(pick(row, "kol", "Kol"), 0),
    rest: toNumber(pick(row, "rest", "Rest"), 0)
  }))
});
